import { zodResolver } from '@hookform/resolvers/zod';
import { defaultValue } from 'Pages/reports/[submodules]/employees/schemas/employees-report-schemas';
import { employeesReportFilterSchema } from 'Pages/reports/[submodules]/employees/schemas/employees-report-schemas';
import { useForm } from 'react-hook-form';
import useTerminalsApi from './useTerminalsApi';
import { EmployeeReportFilterI } from 'types';
import useDepartmentsApi from './useDepartmentsApi';
import useJobTitlesApi from './useJobTitlesApi';
import useJobTitlesTypeApi from './useJobTitlesTypeApi';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from './useDebounce';
import { useEffect } from 'react';
interface Props {
  onSearch: (filters: EmployeeReportFilterI) => void;
}

const useEmployeesReportFilters = ({ onSearch }: Props) => {
  const { t2 } = useTranslation('employees_reports');

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
    watch,
  } = useForm({
    resolver: zodResolver(employeesReportFilterSchema),
    defaultValues: defaultValue,
  });

  const { data: terminalsData, loading: terminalsLoading, error: terminalsError } = useTerminalsApi({ init: true });

  const {
    data: departmentsData,
    error: departmentsError,
    loading: departmentsLoading,
  } = useDepartmentsApi({ init: true });

  const { data: jobTitlesData, loading: jobTitlesLoading, error: jobTitlesError } = useJobTitlesApi({ init: true });

  const {
    data: jobTitlesTypesData,
    loading: jobTitlesTypesLoading,
    error: jobTitlesTypesError,
  } = useJobTitlesTypeApi({ init: true });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  // Watch for changes in the name field
  const nameValue = watch('fullName');
  const debouncedName = useDebounce(nameValue, 500);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getValues();
      onSearch(values);
    }
  }, [debouncedName]);

  // Handle other filter changes immediately
  const handleFilterChange = () => {
    const values = getValues();
    onSearch(values);
  };

  return {
    control,
    reset,
    setValue,
    getValues,
    isDirty,
    handleReset,
    t2,
    // Data
    terminalsData,
    departmentsData,
    jobTitlesData,
    jobTitlesTypesData,
    // Loading
    terminalsLoading,
    departmentsLoading,
    jobTitlesLoading,
    jobTitlesTypesLoading,
    // Error
    terminalsError,
    departmentsError,
    jobTitlesError,
    jobTitlesTypesError,
    handleFilterChange,
  };
};

export default useEmployeesReportFilters;
